/**
 * @fileoverview Mixin principal del flujo de autenticación.
 * Orquesta recaptcha, llamada al backend, navegación post-login
 * y delega el manejo de errores a login-lock-handler.mixin.js
 * y el tracking a login-analytics.service.js.
 * @module mixins/login.mixin
 */

import { mapActions, mapGetters } from 'vuex'
import DocumentRules from '../interfaces/documentRules'
import { isPermanentLock, isAttemptsLock } from '../interfaces/loginResponse'
import { toDocumentType } from '../interfaces/authMappers'
import {
  trackLoginAttempt,
  trackLoginSuccess,
  // trackLoginError,
  trackLoginPageView
} from '../services/login-analytics.service'
import { RECAPTCHA_ACTIONS } from '../constants'

export default {
  data () {
    return {
      loading: false,
      loadingRegistro: false,
      activeBtnLogin: false,
      activeBtnCreateUser: false,
      txtUserLogin: '',
      txtPassLogin: '',
      showPassword: false,
      typePassword: 'password',
      inconvenientes: false,
      error: {
        showModalGeneric: false,
        showModal: false,
        title: '',
        parraf: '',
        secondButton: null,
        messageButton: 'Entendido'
      },
      promotions: {
        participa: false
      },
      flagPopupAlert: false,
      infoPopup: {},
      scrollPosition: null,
      form: {
        tipoDocumento: 1,
        numeroDocumento: null,
        clave: '',
        token: '',
        saveData: true
      },
      dl: null,
      v: null
    }
  },
  computed: {
    ...mapGetters('user', ['userInfo'])
  },
  methods: {
    ...mapActions('user', ['userExists', 'getUserInfo', 'getTypeOfClient', 'getIP']),
    ...mapActions('user', { authLogin: 'login' }),

    scrollToForm () {
      const element = this.$refs.formLogin
      if (!element) return
      const top = element.offsetTop
      if (window.screen.width < 900) {
        window.scrollTo({ top, behavior: 'smooth' })
      }
    },

    alphanumericValidation (event) {
      if (!event.key.match(/[0-9a-zA-Z]/g)) {
        event.preventDefault()
      }
    },

    getUserLogin () {
      if (this.txtPassLogin.length >= 1 && this.txtUserLogin.length >= 7) {
        this.activeBtnLogin = true
        this.activeBtnCreateUser = true
      } else if (this.txtUserLogin.length >= 7) {
        this.activeBtnCreateUser = true
      } else {
        this.activeBtnLogin = false
        this.activeBtnCreateUser = false
      }
    },

    getPassLogin () {
      this.activeBtnLogin = this.txtPassLogin.length >= 1 && this.txtUserLogin.length >= 8
    },

    showHidePassword () {
      this.showPassword = !this.showPassword
      this.typePassword = this.showPassword ? 'text' : 'password'
      this.icons.eyeSelected = this.showPassword ? this.icons.eyeOpen : this.icons.eyeClose
    },

    closeAlertPopupMethod () {
      this.flagPopupAlert = false
    },

    /**
     * Genera un token de recaptcha único por intento de login.
     * Limpia cualquier token previo garantizando unicidad.
     * @param {string} actionType - Tipo de acción (RECAPTCHA_ACTIONS.LOGIN por defecto)
     * @returns {Promise<string>} Token generado o cadena vacía si falla
     */
    async recaptchaMethod (actionType = RECAPTCHA_ACTIONS.LOGIN) {
      this.form.token = ''

      if (process.env.ENVIRONMENT !== 'dev') {
        try {
          await this.$recaptchaLoaded()
          if (window.grecaptcha && window.grecaptcha.enterprise) {
            try {
              await window.grecaptcha.enterprise.ready(() => {})
            } catch (e) {
              console.log('grecaptcha.enterprise.ready error (ignorable):', e)
            }
          }
          this.form.token = await this.$recaptcha(actionType)
        } catch (e) {
          console.error('Login recaptcha error:', e)
          this.form.token = ''
        }
      }

      return this.form.token
    },

    /**
     * Orquestador principal del proceso de login.
     * Valida datos, obtiene recaptcha, llama al backend
     * y delega respuestas exitosas y errores a los métodos correspondientes.
     */
    async login () {
      const temporalNumberDocument = (this.txtUserLogin || '').trim()

      trackLoginAttempt(this.$gtm, temporalNumberDocument)

      this.loading = true
      this.form.numeroDocumento = temporalNumberDocument
      this.form.clave = this.txtPassLogin
      this.form.tipoDocumento = DocumentRules.getDocumentTypeZS(this.form.numeroDocumento)

      if (process.env.ENVIRONMENT !== 'dev') {
        try {
          await this.recaptchaMethod()
        } catch (e) {
          this.form.token = ''
        }
        if (!this.form.token) {
          this.loading = false
          this._handleSecurityCheckError()
          return
        }
      }

      // CR Auth solo soporta DNI y CE: un RUC o un documento inválido no se envía
      if (this.form.tipoDocumento < 0 || !toDocumentType(this.form.tipoDocumento)) {
        this.form.token = ''
        this.loading = false
        this.handleInvalidDocument()
        return
      }

      return this.authLogin(this.form)
        .then(res => this._onLoginSuccess(res))
        .catch(err => this._onLoginError(err))
        .finally(() => {
          this.form.token = ''
        })
    },

    /**
     * Maneja la respuesta exitosa del backend (codigoRespuesta = '01').
     * @param {Object} res - Respuesta Axios
     */
    _onLoginSuccess (res) {
      if (res.data.codigoRespuesta !== '01') {
        this.loading = false
        this.$swal({
          imageUrl: this.icons.aviso,
          titleText: 'Atención',
          text: 'Para iniciar sesión, por favor recupera tu contraseña, así podrás tener una mejor experiencia.',
          confirmButtonText: 'Salir'
        }).then(() => {
          this.$router.push('/iniciar').catch(() => {})
        })
        return
      }

      const isFirstLogin = localStorage.getItem('signup_init') === '1'
      trackLoginSuccess(
        this.$gtm,
        this.$amplitude,
        res.data,
        this.txtUserLogin,
        this.form.tipoDocumento,
        isFirstLogin
      )
      if (isFirstLogin) localStorage.removeItem('signup_init')

      this.loading = false

      if (res.data.claveProximaVencer === true && res.data.diasRestantesContraseña != null) {
        this._handlePasswordExpiringSoon(res.data.diasRestantesContraseña)
        return
      }

      this._navigateAfterLogin()
    },

    /**
     * Muestra aviso de contraseña próxima a vencer y redirige a Mi Perfil.
     * @param {number} diasRestantes
     */
    _handlePasswordExpiringSoon (diasRestantes) {
      let titleText = `Tu contraseña expirará en ${diasRestantes} días`
      if (diasRestantes === 0) titleText = 'Tu contraseña expira hoy'
      if (diasRestantes === 1) titleText = 'Tu contraseña expirará en 1 día'

      this.$swal({
        titleText,
        text: 'Para mantener tu cuenta segura y evitar restricciones, te recomendamos actualizarla ahora.',
        confirmButtonText: 'Actualizar contraseña',
        showCancelButton: false
      }).then(() => {
        const destination = this.dl ? `/${this.dl}?renovar=true` : '/mi-perfil?renovar=true'
        this.getUserInfo(this.userInfo)
          .then(res => {
            const route = res.data.codigoRespuesta === '01' ? destination : '/mi-perfil?renovar=true'
            this.$router.push(route).catch(() => {})
          })
          .catch(() => this.$router.push('/mi-perfil?renovar=true').catch(() => {}))
      })
    },

    /**
     * Navega a la ruta de destino tras el login exitoso.
     */
    _navigateAfterLogin () {
      this.getUserInfo(this.userInfo)
        .then(res => {
          const destination = this.dl ? `/${this.dl}` : '/inicio'
          const route = res.data.codigoRespuesta === '01' ? destination : '/inicio'
          this.$router.push(route).catch(() => {})
        })
        .catch(() => this.$router.push('/inicio').catch(() => {}))
    },

    /**
     * Maneja todos los errores del login de CR Auth.
     * La action user/login los entrega con la forma de error del login anterior (ver authMappers.toLegacyLoginError).
     * @param {Object} err - Error con `status` y `data`
     */
    _onLoginError (err) {
      this.loading = false

      const responseData = (err.response && err.response.data) || err.data || {}

      if (responseData.claveVencida === true) {
        this._handleExpiredPassword(responseData)
        return
      }

      if (responseData.claveNoRegistrada === true) {
        this._handleExpiredPassword({
          mensajeRespuesta: 'Para iniciar sesión, por favor recupera tu contraseña, así podrás tener una mejor experiencia.'
        })
        return
      }

      if (responseData.captchaInvalido === true) {
        this._handleSecurityCheckError()
        return
      }

      if (responseData.codigoRespuesta === '99') {
        if (isPermanentLock(responseData)) {
          this.handlePermanentLock(responseData)
          return
        }
        if (isAttemptsLock(responseData)) {
          this.handleTemporaryLock(responseData)
          return
        }
        this.handleFailedAttempt(responseData)
        return
      }

      this.handleHttpError(err.status)
    },

    /**
     * Muestra el aviso de que no se pudo completar la verificación de seguridad (reCAPTCHA).
     */
    _handleSecurityCheckError () {
      this.$swal({
        titleText: '¡Lo sentimos!',
        text: 'No se pudo completar la verificación de seguridad. Por favor, intenta nuevamente.',
        confirmButtonText: 'Entendido'
      })
    },

    /**
     * Muestra aviso de contraseña vencida y redirige al flujo de recuperación.
     * @param {Object} responseData
     */
    _handleExpiredPassword (responseData) {
      this.$swal({
        titleText: '¡Lo sentimos!',
        text: responseData.mensajeRespuesta || 'Tu contraseña ha expirado. Para poder ingresar a tu cuenta, actualízala ahora.',
        confirmButtonText: 'Actualizar contraseña',
        showCancelButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false
      }).then(() => {
        this.$router.push({
          name: 'SignUp',
          params: {
            source: 'recovery',
            numeroDocumento: this.form.numeroDocumento,
            tipoDocumento: this.form.tipoDocumento
          }
        }).catch(() => {})
      })
    },

    /**
     * Navega a la pantalla de recuperación de contraseña.
     * Cierra los modales de bloqueo antes de navegar.
     */
    goToRecoveryPassword () {
      this.lockInfo.showCountdown = false
      this.failedAttempt.showModal = false

      this.$gtm.trackEvent({
        event: 'virtualEvent',
        category: 'Zona Segura',
        action: 'Inicio de sesión',
        label: 'Olvidé mi contraseña'
      })
      this.$gtm.trackEvent({
        event: 'select_button',
        'Nombre Sección': 'Zona Segura',
        TipoElemento: 'Botón',
        TipoAccion: 'Inicio de sesión',
        CTA: 'Olvidé mi contraseña'
      })

      this.loadingRegistro = true
      const numeroDocumento = this.txtUserLogin
      const tipoDocumento = DocumentRules.getDocumentTypeZS(numeroDocumento)

      if (this.txtUserLogin !== '' && this.txtUserLogin.length > 7) {
        this.loadingRegistro = false
        this.$router.push({
          name: 'SignUp',
          params: {
            source: 'recovery',
            numeroDocumento,
            tipoDocumento,
            nombreUsuario: 'Cliente',
            apellidoPaterno: '',
            apellidoMaterno: ''
          }
        }).catch(() => {})
        return
      }

      this.loadingRegistro = false
      this.error.title = '¡Parece que nos hace falta un dato!'
      this.error.parraf = 'Ingresa un número de documento válido para poder recuperar tu contraseña.'
      this.error.showModalGeneric = true
    },

    /**
     * Navega a la pantalla de registro con los datos del formulario pre-llenados.
     */
    createAccount () {
      this.loadingRegistro = true
      const numeroDocumento = (this.txtUserLogin || '').trim()
      const tipoDocumento = DocumentRules.getDocumentTypeZS(numeroDocumento)

      this.$gtm.trackEvent({
        event: 'virtualEvent',
        category: 'Zona Segura',
        action: 'Inicio de sesión',
        label: 'Crear una cuenta nueva'
      })
      this.$gtm.trackEvent({
        event: 'select_button',
        'Nombre Sección': 'Zona Segura',
        TipoElemento: 'Botón',
        TipoAccion: 'Inicio de sesión',
        CTA: 'Crear una cuenta nueva'
      })

      if (numeroDocumento === '' || numeroDocumento.length <= 7 || tipoDocumento < 0) {
        this.loadingRegistro = false
        this.error.title = '¡Parece que nos hace falta un dato!'
        this.error.parraf = 'Ingresa un número de documento válido para poder registrarte.'
        this.error.showModalGeneric = true
        return
      }

      this.loadingRegistro = false
      this.$router.push({
        name: 'SignUp',
        params: {
          source: 'signup',
          nombreUsuario: 'Cliente',
          numeroDocumento,
          tipoDocumento,
          apellidoPaterno: '',
          apellidoMaterno: ''
        }
      }).catch(() => {})
    },

    goToSignUp () {
      this.error.showModal = false
      this.error.showModalGeneric = false
      this.createAccount()
    },

    dismissModal () {
      this.error.showModalGeneric = false
      this.error.secondButton = null
      this.error.messageButton = 'Entendido'
    },

    /**
     * Inicializa el estado de la página de login (llamar en mounted).
     */
    initLoginPage () {
      window.addEventListener('scroll', this.updateScroll)
      this.dl = this.$route.query.dl
      this.v = this.$route.query.v
      localStorage.removeItem('attempt_login')
      trackLoginPageView(this.$gtm, this.$amplitude)
      this.getIP().then(res => { localStorage.setItem('ipaddress', res.data.clientIp) }).catch(() => {})
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/login.mixin.js